import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { persistedPageLayouts, type PageLayout as SimplePageLayout } from '../mockData/objectManagerLayouts';
import { allObjectFields } from '../mockData/objectManagerFields';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { normalizeToGobodoFormat, normalizeFromGobodoFormat } from '../../utils/objectApiNameNormalizer';
import {
  fetchPageLayoutTemplates,
  createPageLayoutTemplate,
  fetchPageLayoutTemplate,
  updatePageLayoutTemplate,
  deletePageLayoutTemplate,
} from '../api/uiConfigurationApi';
import type { PageLayoutTemplate } from '../api/uiConfigurationApi';
import { syncLayoutSections } from '../gobodo/gobodoPageLayoutService';

// ============================================
// TYPES
// ============================================

export interface PageLayoutSection {
  id: string;
  label: string;
  order: number;
  columns: 1 | 2;
  collapsible: boolean;
  collapsed?: boolean;
  fields: PageLayoutField[];
}

export interface PageLayoutField {
  id: string;
  fieldId: string; // Reference to CustomField
  fieldName: string;
  fieldLabel: string;
  order: number;
  column: 1 | 2;
  required: boolean;
  readonly: boolean;
  spanBothColumns?: boolean;
}

export interface PageLayout {
  id: string;
  objectId: string;
  name: string;
  description?: string;
  applicationName?: string; // Which app uses this layout (global, cybersweep, credit-repair, id-theft, credit-development, credit-block, all)
  isDefault: boolean;
  sections: PageLayoutSection[];
  createdBy: string;
  createdDate: string;
  lastModified: string;
  assignedTo: string[]; // Profile/Role IDs
}

function normalizeLayoutApplication(app?: string): string | undefined {
  if (!app) return undefined;
  const normalized = app.trim().toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');
  if (normalized === 'all') return 'global';
  if (normalized === 'cyber-sweep') return 'cybersweep';
  if (normalized === 'credit-block') return 'credit-block';
  return normalized;
}

// ============================================
// HELPERS (objectId <-> objectTypeKey)
// Uses normalizer so layout keys align with backend and REACT_APP_GOBODO_CUSTOM_SUFFIX.
// ============================================

const LAYOUT_OBJECT_BASES = ['Lead', 'Client', 'Contact', 'Opportunity', 'Case', 'Event', 'Task', 'Report'] as const;

function baseToObjId(base: string): string {
  const normalized = base.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
  return `obj_${normalized}`;
}

function objectIdToObjectTypeKey(objectId: string): string {
  const objIdToBase: Record<string, string> = {};
  LAYOUT_OBJECT_BASES.forEach(b => { objIdToBase[baseToObjId(b)] = b; });
  if (objIdToBase[objectId]) return normalizeToGobodoFormat(objIdToBase[objectId]);
  if (objectId.startsWith('obj_')) {
    const base = objectId.replace(/^obj_/, '');
    const words = base.split('_');
    const capitalized = words.map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join('');
    return normalizeToGobodoFormat(capitalized);
  }
  return objectId;
}

function objectTypeKeyToObjectId(objectTypeKey: string): string {
  const keyToObjId: Record<string, string> = {};
  LAYOUT_OBJECT_BASES.forEach(b => { keyToObjId[normalizeToGobodoFormat(b)] = baseToObjId(b); });
  if (keyToObjId[objectTypeKey]) return keyToObjId[objectTypeKey];
  const base = normalizeFromGobodoFormat(objectTypeKey);
  if (base !== objectTypeKey) {
    const normalized = base.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
    return `obj_${normalized}`;
  }
  return objectTypeKey;
}

/** Map from object API name (with or without suffix) to obj_ id for layout lookups */
function getLayoutObjectIdMap(): Record<string, string> {
  const map: Record<string, string> = {};
  LAYOUT_OBJECT_BASES.forEach(b => {
    map[b] = baseToObjId(b);
    map[normalizeToGobodoFormat(b)] = baseToObjId(b);
  });
  return map;
}

/** Map from obj_ id to normalized object API name */
function getLayoutObjectApiNameMap(): Record<string, string> {
  const map: Record<string, string> = {};
  LAYOUT_OBJECT_BASES.forEach(b => { map[baseToObjId(b)] = normalizeToGobodoFormat(b); });
  return map;
}

/**
 * Map API PageLayoutTemplate to layoutQueries PageLayout
 */
function templateToPageLayout(template: PageLayoutTemplate, objectId: string): PageLayout {
  const sections: PageLayoutSection[] = (template.sections || []).map((sec, si) => ({
    id: sec.id || `sec_${si}`,
    label: (sec as any).label ?? sec.name ?? 'Section',
    order: sec.order ?? si + 1,
    columns: (sec.columns === 3 || sec.columns === 4 ? 2 : sec.columns) as 1 | 2,
    collapsible: sec.collapsible ?? false,
    collapsed: (sec as any).defaultCollapsed ?? (sec as any).collapsed,
    fields: (sec.fields || []).map((f: any, fi: number) => ({
      id: `lf_${sec.id}_${fi}`,
      fieldId: f.fieldId || f.id || `field_${fi}`,
      fieldName: f.fieldKey || f.fieldId || '',
      fieldLabel: f.label || f.fieldName || '',
      order: f.order ?? fi + 1,
      column: 1 as 1 | 2,
      required: f.required ?? false,
      readonly: false,
    })),
  }));
  return {
    id: template.id,
    objectId,
    name: template.name,
    description: template.description,
    applicationName: template.application,
    isDefault: template.isDefault ?? false,
    sections,
    createdBy: template.createdBy ?? 'system',
    createdDate: template.createdAt ?? new Date().toISOString(),
    lastModified: template.updatedAt ?? new Date().toISOString(),
    assignedTo: ['all'],
  };
}

// ============================================
// TYPE ADAPTERS
// ============================================

/**
 * Convert simple page layout (from objectManagerLayouts) to detailed format (for UI)
 */
function convertSimpleLayoutToDetailed(
  simpleLayout: SimplePageLayout,
  objectFields: any[]
): PageLayout {
  const sections: PageLayoutSection[] = simpleLayout.sections.map(section => {
    const fields: PageLayoutField[] = section.fields.map((fieldName, index) => {
      const fieldDef = objectFields.find(f => f.fieldName === fieldName);

      return {
        id: `lf_${section.id}_${fieldName}`,
        fieldId: fieldDef?.id || `field_${fieldName}`,
        fieldName: fieldName,
        fieldLabel: fieldDef?.fieldLabel || fieldName,
        order: index + 1,
        column: (index % section.columns) + 1 as 1 | 2,
        required: fieldDef?.required || false,
        readonly: fieldDef?.readonly || false
      };
    });

    return {
      id: section.id,
      label: section.title,
      order: section.order,
      columns: section.columns,
      collapsible: section.collapsible || false,
      collapsed: section.defaultCollapsed,
      fields
    };
  });

  // Map object API name to object ID (handles both with and without __c suffix)
  const objectIdMap = getLayoutObjectIdMap();

  // Handle null/undefined objectApiName
  const objectApiName = simpleLayout.objectApiName || '';
  
  // Generate object ID: use map if available, otherwise generate from API name
  let objectId: string;
  if (objectIdMap[objectApiName]) {
    objectId = objectIdMap[objectApiName];
  } else if (objectApiName) {
    // For custom objects: convert "CustomThing__c" -> "obj_custom_thing" or "CustomThing" -> "obj_custom_thing"
    const baseName = normalizeFromGobodoFormat(objectApiName);
    const normalized = baseName
      .replace(/([A-Z])/g, '_$1') // Insert underscore before capital letters
      .toLowerCase()
      .replace(/^_/, ''); // Remove leading underscore
    objectId = `obj_${normalized}`;
  } else {
    // Fallback for empty objectApiName
    objectId = 'obj_unknown';
  }

  return {
    id: simpleLayout.id,
    objectId: objectId,
    name: simpleLayout.name,
    description: simpleLayout.description || `Layout for ${simpleLayout.objectApiName} detail pages`,
    applicationName: simpleLayout.applicationName || 'all',
    isDefault: simpleLayout.isDefault || false,
    sections,
    assignedTo: ['all'],
    createdBy: simpleLayout.createdBy || 'System',
    createdDate: simpleLayout.createdDate || new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: simpleLayout.lastModifiedDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
  };
}

// ============================================
// QUERY KEYS
// ============================================

export const layoutKeys = {
  all: ['layouts'] as const,
  byObject: (objectId: string) => [...layoutKeys.all, 'object', objectId] as const,
  detail: (layoutId: string) => [...layoutKeys.all, 'detail', layoutId] as const,
};

// ============================================
// HOOKS
// ============================================

/**
 * Get all layouts for an object
 * Future: Replace with API call to GET /api/objects/:objectId/layouts
 */
export function useLayoutsByObject(objectId: string, filters?: {
  applicationName?: string;
}) {
  return useQuery({
    queryKey: [...layoutKeys.byObject(objectId), JSON.stringify(filters || {})],
    queryFn: async () => {
      if (USE_GOBODO_API) {
        try {
          const { data: templates } = await fetchPageLayoutTemplates({
            objectId,
            application: filters?.applicationName && filters.applicationName !== 'all' ? filters.applicationName as any : undefined,
          });
          const layouts = templates.map(t => templateToPageLayout(t, objectId));
          return layouts.sort((a, b) => {
            if (a.isDefault && !b.isDefault) return -1;
            if (!a.isDefault && b.isDefault) return 1;
            return a.name.localeCompare(b.name);
          });
        } catch (e) {
          console.warn('Gobodo layouts fetch failed, using mock:', e);
        }
      }

      await new Promise(resolve => setTimeout(resolve, 400));

      // Map object ID to object API name (normalized to match backend/env suffix)
      const objectApiNameMap = getLayoutObjectApiNameMap();

      // Generate API name: use map if available, otherwise generate from objectId for custom objects
      let objectApiName: string;
      if (objectApiNameMap[objectId]) {
        objectApiName = objectApiNameMap[objectId];
      } else if (objectId.startsWith('obj_')) {
        // For custom objects: convert "obj_custom_thing" -> "CustomThing" then normalize
        const baseName = objectId.replace(/^obj_/, ''); // Remove "obj_" prefix
        const words = baseName.split('_');
        const capitalized = words.map(word =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join('');
        objectApiName = normalizeToGobodoFormat(capitalized);
      } else {
        // Fallback: use objectId as-is
        objectApiName = objectId;
      }

      console.log('📐 PAGE LAYOUTS DEBUG:');
      console.log('  Object ID:', objectId);
      console.log('  Mapped Object API Name:', objectApiName);
      console.log('  Filters:', filters);

      // Get persisted layouts for this object
      const allLayouts = persistedPageLayouts.get();
      console.log('  Total layouts in storage:', allLayouts.length);
      console.log('  All stored layouts:', allLayouts.map(l => ({ id: l.id, name: l.name, objectApiName: l.objectApiName })));

      let simpleLayouts = allLayouts.filter(l => l.objectApiName === objectApiName);
      console.log(`  Layouts for ${objectApiName}:`, simpleLayouts.length);
      console.log('  Matching layouts:', simpleLayouts.map(l => ({ id: l.id, name: l.name })));

      // Get fields for this object
      const objectFields = allObjectFields[objectId] || [];
      console.log('  Object fields available:', objectFields.length);

      // Convert simple layouts to detailed format
      let layouts = simpleLayouts.map(sl => convertSimpleLayoutToDetailed(sl, objectFields));
      console.log('  Converted layouts:', layouts.length);

      // Apply filters
      if (filters?.applicationName && filters.applicationName !== 'all') {
        const beforeFilter = layouts.length;
        // Only show layouts for the selected application (exclude Global/all layouts)
        layouts = layouts.filter(l => l.applicationName === filters.applicationName);
        console.log(`  After application filter (${filters.applicationName}): ${beforeFilter} → ${layouts.length}`);
      }

      // Sort: default first, then alphabetically
      const sorted = layouts.sort((a, b) => {
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        return a.name.localeCompare(b.name);
      });

      console.log('  Final layouts returned:', sorted.map(l => ({ id: l.id, name: l.name, isDefault: l.isDefault })));

      return sorted;
    },
    staleTime: 3 * 60 * 1000,
    enabled: !!objectId,
  });
}

/**
 * Get single layout
 * Future: Replace with API call to GET /api/layouts/:layoutId
 */
export function useLayout(layoutId: string) {
  return useQuery({
    queryKey: layoutKeys.detail(layoutId),
    queryFn: async () => {
      if (USE_GOBODO_API) {
        try {
          const { data: template } = await fetchPageLayoutTemplate(layoutId);
          const objectId = template.objectTypeKey ? objectTypeKeyToObjectId(template.objectTypeKey) : 'obj_unknown';
          return templateToPageLayout(template, objectId);
        } catch (e) {
          console.warn('Gobodo layout fetch failed, using mock:', e);
        }
      }

      await new Promise(resolve => setTimeout(resolve, 300));

      // Get all persisted layouts
      const allLayouts = persistedPageLayouts.get();
      const simpleLayout = allLayouts.find(l => l.id === layoutId);

      if (!simpleLayout) {
        throw new Error('Layout not found');
      }

      // Map object API name to object ID
      const objectIdMap = getLayoutObjectIdMap();

      // Handle null/undefined objectApiName
      const objectApiName = simpleLayout.objectApiName || '';
      
      // Generate object ID: use map if available, otherwise generate from API name
      let objectId: string;
      if (objectIdMap[objectApiName]) {
        objectId = objectIdMap[objectApiName];
      } else if (objectApiName) {
        // For custom objects: convert "CustomThing__c" -> "obj_custom_thing" or "CustomThing" -> "obj_custom_thing"
        const baseName = normalizeFromGobodoFormat(objectApiName);
        const normalized = baseName
          .replace(/([A-Z])/g, '_$1') // Insert underscore before capital letters
          .toLowerCase()
          .replace(/^_/, ''); // Remove leading underscore
        objectId = `obj_${normalized}`;
      } else {
        // Fallback for empty objectApiName
        objectId = 'obj_unknown';
      }
      
      const objectFields = allObjectFields[objectId] || [];

      return convertSimpleLayoutToDetailed(simpleLayout, objectFields);
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!layoutId,
  });
}

/**
 * Create new layout
 * Future: Replace with API call to POST /api/objects/:objectId/layouts
 */
export function useCreateLayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (layoutData: Omit<PageLayout, 'id' | 'createdBy' | 'createdDate' | 'lastModified'>) => {
      if (USE_GOBODO_API) {
        try {
          const objectTypeKey = objectIdToObjectTypeKey(layoutData.objectId);
          const application = normalizeLayoutApplication(layoutData.applicationName) ?? 'global';
          const { data: template } = await createPageLayoutTemplate({
            name: layoutData.name,
            description: layoutData.description,
            application: application as any,
            objectTypeKey,
            isDefault: layoutData.isDefault ?? false,
            isPublic: false,
            sections: [],
          });
          // Persist initial sections immediately to avoid opening an empty builder after create.
          if ((layoutData.sections || []).length > 0) {
            await syncLayoutSections(
              template.id,
              layoutData.sections.map(s => ({
                id: s.id,
                label: s.label,
                order: s.order,
                columns: s.columns,
                collapsible: s.collapsible,
                collapsed: s.collapsed,
                fields: (s.fields || []).map((f, index) => ({
                  fieldId: f.fieldId,
                  fieldName: f.fieldName,
                  fieldLabel: f.fieldLabel,
                  order: index + 1,
                  required: f.required,
                })),
              }))
            );
          }
          const { data: hydratedTemplate } = await fetchPageLayoutTemplate(template.id);
          return templateToPageLayout(hydratedTemplate, layoutData.objectId);
        } catch (e) {
          console.warn('Gobodo create layout failed, using mock:', e);
        }
      }

      await new Promise(resolve => setTimeout(resolve, 600));

      // Map object ID to API name
      const objectApiNameMap = getLayoutObjectApiNameMap();

      // Generate API name: use map if available, otherwise generate from objectId for custom objects
      let objectApiName: string;
      if (objectApiNameMap[layoutData.objectId]) {
        objectApiName = objectApiNameMap[layoutData.objectId];
      } else if (layoutData.objectId.startsWith('obj_')) {
        // For custom objects: convert "obj_custom_thing" -> "CustomThing__c"
        const baseName = layoutData.objectId.replace(/^obj_/, ''); // Remove "obj_" prefix
        const words = baseName.split('_');
        const capitalized = words.map(word =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join('');
        objectApiName = normalizeToGobodoFormat(capitalized);
      } else {
        // Fallback: use objectId as-is
        objectApiName = layoutData.objectId;
      }

      // Convert to simple format for storage
      const simpleLayout: SimplePageLayout = {
        id: `layout_${Date.now()}`,
        name: layoutData.name,
        objectApiName: objectApiName,
        description: layoutData.description,
        applicationName: layoutData.applicationName,
        isDefault: layoutData.isDefault || false,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        lastModifiedBy: 'Current User',
        lastModifiedDate: new Date().toISOString(),
        sections: layoutData.sections.map(section => ({
          id: section.id,
          title: section.label,
          order: section.order,
          columns: section.columns,
          collapsible: section.collapsible,
          defaultCollapsed: section.collapsed,
          fields: section.fields.map(f => f.fieldName)
        }))
      };

      // Add to persistedPageLayouts
      const allLayouts = persistedPageLayouts.get();
      persistedPageLayouts.set([...allLayouts, simpleLayout]);

      // Convert back to detailed format for return
      const objectFields = allObjectFields[layoutData.objectId] || [];
      return convertSimpleLayoutToDetailed(simpleLayout, objectFields);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: layoutKeys.byObject(data.objectId) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });

      // Force refetch detail page cache
      const objectApiNameMap: Record<string, string> = {
        'obj_lead': 'Lead',
        'obj_client': 'Client',
        'obj_contact': 'Contact',
        'obj_opportunity': 'Opportunity',
        'obj_case': 'Case',
        'obj_event': 'Event',
        'obj_task': 'Task',
        'obj_report': 'Report'
      };
      const objectApiName = objectApiNameMap[data.objectId];
      if (objectApiName) {
        queryClient.invalidateQueries({ queryKey: ['pageLayout', objectApiName] });
        queryClient.refetchQueries({
          queryKey: ['pageLayout', objectApiName],
          type: 'active'
        });
      }
    },
  });
}

/**
 * Update layout
 * Future: Replace with API call to PATCH /api/layouts/:layoutId
 */
export function useUpdateLayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ layoutId, updates }: { layoutId: string; updates: Partial<PageLayout> }) => {
      if (USE_GOBODO_API) {
        try {
          const apiUpdates: Partial<PageLayoutTemplate> = {};
          if (updates.name != null) apiUpdates.name = updates.name;
          if (updates.description != null) apiUpdates.description = updates.description;
          if (updates.applicationName != null) apiUpdates.application = normalizeLayoutApplication(updates.applicationName) as any;
          if (updates.isDefault != null) apiUpdates.isDefault = updates.isDefault;
          if (Object.keys(apiUpdates).length > 0) {
            await updatePageLayoutTemplate(layoutId, apiUpdates);
          }
          if (updates.sections !== undefined) {
            await syncLayoutSections(
              layoutId,
              updates.sections.map(s => ({
                id: s.id,
                label: s.label,
                order: s.order,
                columns: s.columns,
                collapsible: s.collapsible,
                collapsed: s.collapsed,
                fields: (s.fields || []).map((f, index) => ({
                  fieldId: f.fieldId,
                  fieldName: f.fieldName,
                  fieldLabel: f.fieldLabel,
                  order: index + 1,
                  required: f.required,
                })),
              }))
            );
          }
          const { data: template } = await fetchPageLayoutTemplate(layoutId);
          const objectId = template.objectTypeKey ? objectTypeKeyToObjectId(template.objectTypeKey) : updates.objectId ?? 'obj_unknown';
          const result = templateToPageLayout(template, objectId);

          // Dual-write: sync to localStorage so mock fallback stays consistent
          try {
            const allLayouts = persistedPageLayouts.get();
            const objectApiName = template.objectTypeKey || (result as any).objectApiName || '';
            const simpleLayout: SimplePageLayout = {
              id: layoutId,
              name: template.name,
              objectApiName,
              description: template.description,
              isDefault: template.isDefault,
              applicationName: template.application,
              lastModifiedDate: new Date().toISOString(),
              sections: result.sections.map((s: any) => ({
                id: s.id,
                title: s.title || s.name || '',
                order: s.order || 0,
                columns: s.columns || 2,
                collapsible: s.collapsible ?? false,
                defaultCollapsed: s.defaultCollapsed ?? false,
                fields: s.fields || []
              }))
            };
            const idx = allLayouts.findIndex(l => l.id === layoutId);
            if (idx >= 0) {
              const updated = [...allLayouts];
              updated[idx] = { ...allLayouts[idx], ...simpleLayout };
              persistedPageLayouts.set(updated);
            } else {
              persistedPageLayouts.set([...allLayouts, simpleLayout]);
            }
          } catch (syncErr) {
            console.warn('[useUpdateLayout] Failed to sync layout to localStorage:', syncErr);
          }

          return result;
        } catch (e) {
          console.warn('Gobodo update layout failed:', e);
          throw e;
        }
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      const allLayouts = persistedPageLayouts.get();
      const layoutIndex = allLayouts.findIndex(l => l.id === layoutId);

      if (layoutIndex === -1) {
        throw new Error('Layout not found');
      }

      // Convert updates.sections from detailed format to simple format if provided
      const simpleUpdates: any = { ...updates };
      if (updates.sections) {
        console.log('💾 useUpdateLayout: Converting sections to simple format');
        console.log('  Input sections:', updates.sections.map(s => ({
          id: s.id,
          label: s.label,
          fieldCount: s.fields?.length || 0
        })));

        simpleUpdates.sections = updates.sections.map(section => ({
          id: section.id,
          title: section.label,
          order: section.order,
          columns: section.columns,
          collapsible: section.collapsible,
          defaultCollapsed: section.collapsed,
          fields: section.fields.map(f => f.fieldName)
        }));

        console.log('  Output sections:', simpleUpdates.sections.map((s: any) => ({
          id: s.id,
          title: s.title,
          fields: s.fields
        })));
      }

      // Update the layout
      const updatedLayout: SimplePageLayout = {
        ...allLayouts[layoutIndex],
        ...simpleUpdates,
        lastModifiedDate: new Date().toISOString()
      };

      // Save back to persistedPageLayouts
      const updatedLayouts = [...allLayouts];
      updatedLayouts[layoutIndex] = updatedLayout;
      persistedPageLayouts.set(updatedLayouts);

      // Map object API name to object ID for return value
      const objectIdMap: Record<string, string> = {
        'Lead': 'obj_lead',
        'Client': 'obj_client',
        'Contact': 'obj_contact',
        'Opportunity': 'obj_opportunity',
        'Case': 'obj_case',
        'Event': 'obj_event',
        'Task': 'obj_task',
        'Report': 'obj_report'
      };

      const objectId = objectIdMap[updatedLayout.objectApiName];
      const objectFields = allObjectFields[objectId] || [];

      // Convert to detailed format for return
      return convertSimpleLayoutToDetailed(updatedLayout, objectFields);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: layoutKeys.byObject(data.objectId) });
      queryClient.invalidateQueries({ queryKey: layoutKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });

      // CRITICAL: Force refetch the pageLayout cache used by detail pages
      // Using refetchQueries instead of invalidateQueries to bypass staleTime
      const objectApiNameMap: Record<string, string> = {
        'obj_lead': 'Lead',
        'obj_client': 'Client',
        'obj_contact': 'Contact',
        'obj_opportunity': 'Opportunity',
        'obj_case': 'Case',
        'obj_event': 'Event',
        'obj_task': 'Task',
        'obj_report': 'Report'
      };
      const objectApiName = objectApiNameMap[data.objectId];

      if (objectApiName) {
        // Invalidate first to mark as stale
        queryClient.invalidateQueries({ queryKey: ['pageLayout', objectApiName] });
        // Then force refetch all active queries to bypass staleTime
        queryClient.refetchQueries({
          queryKey: ['pageLayout', objectApiName],
          type: 'active'
        });
      }

      // CRITICAL: Also invalidate the configuration queries cache.
      // DynamicTabRenderer reads from configKeys.page(pageId), not layoutKeys.
      // Without this, app detail pages never see the updated layout.
      queryClient.invalidateQueries({ queryKey: ['configuration'] });
      // Force immediate refetch of any active configuration queries (bypasses staleTime)
      queryClient.refetchQueries({ queryKey: ['configuration'], type: 'active' });
    },
  });
}

/**
 * Delete layout
 * Future: Replace with API call to DELETE /api/layouts/:layoutId
 */
export function useDeleteLayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (layoutId: string) => {
      if (USE_GOBODO_API) {
        try {
          const { data: template } = await fetchPageLayoutTemplate(layoutId);
          const objectId = template.objectTypeKey ? objectTypeKeyToObjectId(template.objectTypeKey) : 'obj_unknown';
          await deletePageLayoutTemplate(layoutId);
          return { layoutId, objectId };
        } catch (e) {
          console.warn('Gobodo delete layout failed, using mock:', e);
        }
      }

      await new Promise(resolve => setTimeout(resolve, 400));

      // Get all layouts
      const allLayouts = persistedPageLayouts.get();
      const layout = allLayouts.find(l => l.id === layoutId);

      if (!layout) {
        throw new Error('Layout not found');
      }

      if (layout.isDefault) {
        throw new Error('Cannot delete default layout');
      }

      // Remove layout
      const updatedLayouts = allLayouts.filter(l => l.id !== layoutId);
      persistedPageLayouts.set(updatedLayouts);

      // Map object API name to object ID
      const objectIdMap: Record<string, string> = {
        'Lead': 'obj_lead',
        'Client': 'obj_client',
        'Contact': 'obj_contact',
        'Opportunity': 'obj_opportunity',
        'Case': 'obj_case',
        'Event': 'obj_event',
        'Task': 'obj_task',
        'Report': 'obj_report'
      };

      const objectId = objectIdMap[layout.objectApiName];

      return { layoutId, objectId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: layoutKeys.byObject(data.objectId) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });

      // Force refetch detail page cache
      const objectApiNameMap: Record<string, string> = {
        'obj_lead': 'Lead',
        'obj_client': 'Client',
        'obj_contact': 'Contact',
        'obj_opportunity': 'Opportunity',
        'obj_case': 'Case',
        'obj_event': 'Event',
        'obj_task': 'Task',
        'obj_report': 'Report'
      };
      const objectApiName = objectApiNameMap[data.objectId];
      if (objectApiName) {
        queryClient.invalidateQueries({ queryKey: ['pageLayout', objectApiName] });
        queryClient.refetchQueries({
          queryKey: ['pageLayout', objectApiName],
          type: 'active'
        });
      }
    },
  });
}

/**
 * Clone layout
 * Future: Replace with API call to POST /api/layouts/:layoutId/clone
 */
export function useCloneLayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (layoutId: string) => {
      if (USE_GOBODO_API) {
        try {
          const { data: template } = await fetchPageLayoutTemplate(layoutId);
          const objectId = template.objectTypeKey ? objectTypeKeyToObjectId(template.objectTypeKey) : 'obj_unknown';
          const { data: newTemplate } = await createPageLayoutTemplate({
            name: `${template.name} (Copy)`,
            description: template.description,
            application: template.application,
            objectTypeKey: template.objectTypeKey,
            isDefault: false,
            isPublic: template.isPublic,
            sections: [],
          });
          return templateToPageLayout(newTemplate, objectId);
        } catch (e) {
          console.warn('Gobodo clone layout failed, using mock:', e);
        }
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      // Get all layouts
      const allLayouts = persistedPageLayouts.get();
      const layout = allLayouts.find(l => l.id === layoutId);

      if (!layout) {
        throw new Error('Layout not found');
      }

      const clonedLayout: SimplePageLayout = {
        ...layout,
        id: `layout_${Date.now()}`,
        name: `${layout.name} (Copy)`,
        isDefault: false
      };

      // Add cloned layout to persisted layouts
      persistedPageLayouts.set([...allLayouts, clonedLayout]);

      // Map object API name to object ID
      const objectIdMap: Record<string, string> = {
        'Lead': 'obj_lead',
        'Client': 'obj_client',
        'Contact': 'obj_contact',
        'Opportunity': 'obj_opportunity',
        'Case': 'obj_case',
        'Event': 'obj_event',
        'Task': 'obj_task',
        'Report': 'obj_report'
      };

      const objectId = objectIdMap[layout.objectApiName];
      const objectFields = allObjectFields[objectId] || [];

      return convertSimpleLayoutToDetailed(clonedLayout, objectFields);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: layoutKeys.byObject(data.objectId) });
    },
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get application name display label
 */
export function getApplicationLabel(appName?: string): string {
  const labels: Record<string, string> = {
    global: 'Global/Shared',
    cybersweep: 'CyberSweep',
    'credit-repair': 'Credit Repair',
    'id-theft': 'ID Theft Recovery',
    'credit-development': 'Credit Development',
    'credit-block': 'Credit Block',
    'regal-main': 'Regal Main',
    all: 'All Applications'
  };
  return labels[appName || 'all'] || appName || 'All Applications';
}

/**
 * Count total fields in layout
 */
export function getTotalFieldCount(layout: PageLayout): number {
  return layout.sections.reduce((total, section) => total + section.fields.length, 0);
}
